import { useState, type ReactElement } from 'react';

export interface InstallBannerProps {
  onClose?: () => void;
  onInstall?: () => void;
  /** iOS Safari no tiene prompt nativo → mostramos pasos manuales. */
  iosFallback?: boolean;
}

type BannerState = 'idle' | 'installing' | 'dismissing';

/** Mini-orbe de marca (lima + anillo violeta sobre fondo oscuro). */
function MiniOrb(): ReactElement {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
      <circle cx="20" cy="20" r="13" fill="#d4f23e" />
      <circle cx="20" cy="20" r="13" fill="none" stroke="#a78bfa" strokeWidth="1.6" opacity="0.7" />
      <ellipse cx="16" cy="16" rx="4" ry="2.6" fill="#ffffff" opacity="0.35" />
    </svg>
  );
}

function ShareGlyph(): ReactElement {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v12M8 7l4-4 4 4" />
      <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
    </svg>
  );
}

export function InstallBanner({
  onClose,
  onInstall,
  iosFallback = false,
}: InstallBannerProps): ReactElement {
  const [state, setState] = useState<BannerState>('idle');
  const [iosSheet, setIosSheet] = useState(false);

  const dismiss = (): void => {
    setState('dismissing');
    window.setTimeout(() => onClose?.(), 220);
  };

  const install = (): void => {
    if (iosFallback) {
      setIosSheet(true);
      return;
    }
    setState('installing');
    window.setTimeout(() => {
      setState('dismissing');
      window.setTimeout(() => onInstall?.(), 220);
    }, 1000);
  };

  return (
    <>
      <div
        className={`pwa-banner ${state === 'dismissing' ? 'pwa-banner--out' : ''}`}
        role="dialog"
        aria-label="Instalar Speak"
      >
        <div className="pwa-banner__icon" aria-hidden="true">
          <MiniOrb />
        </div>
        <div className="pwa-banner__copy">
          <div className="pwa-banner__title">Instalá Speak</div>
          <div className="pwa-banner__sub">Acceso directo desde tu home, sin browser de por medio.</div>
        </div>
        <div className="pwa-banner__actions">
          <button
            type="button"
            className="pwa-btn pwa-btn--ghost"
            onClick={dismiss}
            disabled={state === 'installing'}
          >
            Más tarde
          </button>
          <button
            type="button"
            className="pwa-btn pwa-btn--primary"
            onClick={install}
            disabled={state === 'installing'}
          >
            {state === 'installing' ? <span className="pwa-spinner" aria-hidden="true" /> : null}
            {state === 'installing' ? 'Instalando…' : 'Instalar'}
          </button>
        </div>
      </div>

      {iosSheet ? <IosInstallPopover onClose={() => setIosSheet(false)} onDone={dismiss} /> : null}
    </>
  );
}

interface IosInstallPopoverProps {
  onClose: () => void;
  onDone: () => void;
}

function IosInstallPopover({ onClose, onDone }: IosInstallPopoverProps): ReactElement {
  return (
    <>
      <div className="pwa-scrim" onClick={onClose} aria-hidden="true" />
      <div className="pwa-ios-pop" role="dialog" aria-label="Instalar en iPhone">
        <div className="pwa-ios-pop__head">
          <span className="pwa-eyebrow">Instalar en iPhone</span>
          <button type="button" className="pwa-ios-close" onClick={onClose} aria-label="Cerrar">
            ✕
          </button>
        </div>
        <ol className="pwa-ios-steps">
          <li>
            <span className="pwa-ios-n">1</span>
            <span>
              Tocá el ícono <b>Compartir</b> en Safari.
            </span>
            <span className="pwa-ios-glyph" aria-hidden="true">
              <ShareGlyph />
            </span>
          </li>
          <li>
            <span className="pwa-ios-n">2</span>
            <span>
              Elegí <b>&ldquo;Agregar a inicio&rdquo;</b> en el menú.
            </span>
          </li>
          <li>
            <span className="pwa-ios-n">3</span>
            <span>Confirmá. Vas a verlo en tu home como una app más.</span>
          </li>
        </ol>
        <button
          type="button"
          className="pwa-btn pwa-btn--primary pwa-btn--block"
          onClick={() => {
            onClose();
            onDone();
          }}
        >
          Listo
        </button>
      </div>
    </>
  );
}
